import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Check, Copy, Link2, Loader2, Plus, Store, Unlink } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { KpiCard, SearchField } from '../../../components/design-system/admin';
import { useCrmApi } from '../../../common/hooks/use-crm-api.hook';
import {
  CrmClient, CrmLead, CrmProspectChoice, CrmProspectResult, FLAGS, LEAD_STAGES, PROSPECT_STAGES, eurEquivalent, fmtMonthly, fmtSplit, parseMoney, sumMoney, todayIso,
} from '../../../common/models/crm.model';
import { CrmBoard } from './crm-board';
import { CrmCardBody } from './crm-card';
import { CrmField, CrmRecordSheet } from './crm-record-sheet';
import { CrmVendorDialog } from './crm-vendor-dialog';
import { VendorDemoVisits } from '../demo-sites/demo-visits';

const F = 'ADMIN_CRM.FIELDS.';
const STAGE_OPTIONS = Object.fromEntries(LEAD_STAGES.map((s) => [s.id, s.label]));
const LEAD_FIELDS: CrmField[] = [
  { key: 'company', label: `${F}COMPANY` },
  { key: 'contact', label: `${F}CONTACT` },
  { key: 'email', label: `${F}EMAIL` },
  { key: 'country', label: `${F}COUNTRY`, type: 'country' },
  { key: 'stage', label: `${F}STAGE`, type: 'select', options: STAGE_OPTIONS },
  { key: 'value', label: `${F}VALUE`, placeholder: 'ADMIN_CRM.PLACEHOLDERS.VALUE' },
  { key: 'next', label: `${F}NEXT` },
  { key: 'due', label: `${F}DUE`, type: 'date' },
  { key: 'tags', label: `${F}TAGS_LEADS`, full: true },
  { key: 'notes', label: `${F}NOTES_HISTORY`, type: 'textarea' },
];

const WON = 'won';
const vendorPath = (id: string) => `/vendors/${id}/edit`;
/** A button inside a draggable card: it must neither start a drag nor open the card. */
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

/**
 * The login of a prospect just made from a lead. Its password is shown this
 * once: the API keeps only its hash.
 */
const ProspectLoginDialog: React.FC<{ result: CrmProspectResult | null; onClose: () => void }> = ({ result, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [copied, setCopied] = useState<'email' | 'password' | null>(null);
  const login = result?.login;
  const copy = async (what: 'email' | 'password', value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(what);
      window.setTimeout(() => setCopied(null), 1500);
    } catch { /* the value is on screen to copy by hand */ }
  };
  return (
    <Dialog open={!!login} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('ADMIN_CRM.PROSPECT.LOGIN_TITLE')}</DialogTitle>
          <DialogDescription>{t('ADMIN_CRM.PROSPECT.LOGIN_DESCRIPTION', { name: result?.vendor.name })}</DialogDescription>
        </DialogHeader>
        {login && (
          <dl className="flex flex-col gap-3 text-sm" data-testid="crm-prospect-login">
            {(['email', 'password'] as const).map((key) => (
              <div key={key}>
                <dt className="text-muted-foreground">{t(key === 'email' ? 'ADMIN_CRM.PROSPECT.LOGIN_EMAIL' : 'ADMIN_CRM.PROSPECT.LOGIN_PASSWORD')}</dt>
                <dd className="flex items-center gap-2">
                  <span className="font-mono text-xs break-all">{login[key]}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => copy(key, login[key])}>
                    {copied === key ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
                    {t(copied === key ? 'ADMIN_CRM.PROSPECT.COPIED' : 'ADMIN_CRM.PROSPECT.COPY')}
                  </Button>
                </dd>
              </div>
            ))}
          </dl>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('ADMIN_CRM.PROSPECT.CLOSE')}</Button>
          {result && (
            <Button onClick={() => navigate(vendorPath(result.vendor._id))}>
              <ArrowUpRight data-icon="inline-start" />{t('ADMIN_CRM.PROSPECT.OPEN')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Leads pipeline: KPIs on top (pipeline, MRR, overdue), then the Kanban board.
 * A lead moved to "won" becomes an active client (the API adds it, with the
 * lead's value as its MRR), so the MRR figures are refreshed after such a move.
 * A lead is given its vendor (the one its demo is made under): an existing one,
 * or a new prospect. A vendor belongs to one lead only; the API enforces it.
 */
export const CrmPipelineTab: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useCrmApi();
  const [leads, setLeads] = useState<CrmLead[]>([]);
  const [clients, setClients] = useState<CrmClient[]>([]);
  const [contacts, setContacts] = useState({ total: 0, replied: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const [sort, setSort] = useState('manual');
  const [editing, setEditing] = useState<Partial<CrmLead> | null>(null);
  const [linking, setLinking] = useState<CrmLead | null>(null);
  const [converting, setConverting] = useState(false);
  const [prospect, setProspect] = useState<CrmProspectResult | null>(null);

  const load = useCallback(async () => {
    try {
      const [l, c, ct] = await Promise.all([
        api.list<CrmLead>('leads'),
        api.list<CrmClient>('clients'),
        api.listContacts({ page: 0 }),
      ]);
      setLeads(l || []);
      setClients(c || []);
      setContacts({ total: ct?.total || 0, replied: (ct?.byStatus?.repondu || 0) + (ct?.byStatus?.interesse || 0) });
    } catch {
      toast.error(t('ADMIN_CRM.PIPELINE.LOAD_FAILED'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const reloadClients = async () => {
    try {
      setClients((await api.list<CrmClient>('clients')) || []);
    } catch { /* the KPIs catch up on the next load */ }
  };

  /** After a lead's stage changed on the API: into or out of "won" changes the clients, so the MRR. */
  const afterStageChange = (lead: Partial<CrmLead> | undefined, from: string | undefined, to: string | undefined) => {
    if (!lead || from === to || (from !== WON && to !== WON)) return;
    reloadClients();
    if (to === WON) {
      const value = fmtMonthly(lead.value);
      toast.success(value
        ? t('ADMIN_CRM.PIPELINE.WON', { company: lead.company, value })
        : t('ADMIN_CRM.PIPELINE.WON_NO_VALUE', { company: lead.company }));
    }
  };

  // Pipeline = open deals only; "standby" is real but dormant, so it stays out of the value.
  const kpis = useMemo(() => {
    const open = leads.filter((l) => !['won', 'lost', 'standby'].includes(l.stage));
    const pipe = sumMoney(open.map((l) => l.value));
    const active = clients.filter((c) => (c.status || 'actif') === 'actif');
    const waiting = clients.filter((c) => c.status === 'en_attente');
    const mrr = sumMoney(active.map((c) => c.mrr));
    const wait = sumMoney(waiting.map((c) => c.mrr));
    const overdue = open.filter((l) => l.due && l.due < todayIso()).length;
    return { open, pipe, mrr, wait, active, waiting, overdue };
  }, [leads, clients]);

  const countries = useMemo(() => Array.from(new Set(leads.map((l) => l.country).filter(Boolean))).sort(), [leads]);

  const shown = useMemo(() => {
    const needle = q.toLowerCase();
    const list = leads
      .filter((l) => !country || l.country === country)
      .filter((l) => !needle || JSON.stringify(l).toLowerCase().includes(needle));
    if (sort === 'value') return [...list].sort((a, b) => (parseMoney(b.value)?.n || 0) - (parseMoney(a.value)?.n || 0));
    if (sort === 'company') return [...list].sort((a, b) => a.company.localeCompare(b.company));
    if (sort === 'next') return [...list].sort((a, b) => ((a.due || '9999') < (b.due || '9999') ? -1 : 1));
    return list;
  }, [leads, q, country, sort]);

  const save = async (data: Partial<CrmLead>) => {
    try {
      const { _id, ...fields } = data;
      if (_id) {
        const before = leads.find((l) => l._id === _id);
        const updated = await api.update<CrmLead>('leads', _id, fields);
        setLeads((ls) => ls.map((l) => (l._id === _id ? { ...l, ...updated } : l)));
        if (before?.stage === WON && fields.stage === WON && before.value !== fields.value) reloadClients();
        else afterStageChange({ ...before, ...updated }, before?.stage, fields.stage);
      } else {
        const created = await api.create<CrmLead>('leads', fields);
        setLeads((ls) => [...ls, created]);
        afterStageChange(created, undefined, created.stage);
      }
      setEditing(null);
      toast.success(t('ADMIN_CRM.PIPELINE.SAVED'));
    } catch (e: any) {
      toast.error(e?.message || t('ADMIN_CRM.COMMON.SAVE_FAILED'));
    }
  };

  const remove = async (id: string) => {
    try {
      await api.remove('leads', id);
      setLeads((ls) => ls.filter((l) => l._id !== id));
      setEditing(null);
    } catch {
      toast.error(t('ADMIN_CRM.COMMON.DELETE_FAILED'));
    }
  };

  const moveStage = async (id: string, stage: string) => {
    const lead = leads.find((l) => l._id === id);
    const prev = lead?.stage;
    setLeads((ls) => ls.map((l) => (l._id === id ? { ...l, stage } : l)));
    try {
      await api.update<CrmLead>('leads', id, { stage });
      afterStageChange(lead, prev, stage);
    } catch {
      setLeads((ls) => ls.map((l) => (l._id === id && prev ? { ...l, stage: prev } : l)));
      toast.error(t('ADMIN_CRM.COMMON.MOVE_FAILED'));
    }
  };

  const reorder = async (updates: { id: string; sort: number; stage?: string }[]) => {
    const before = leads;
    setLeads((ls) => ls.map((l) => {
      const u = updates.find((x) => x.id === l._id);
      return u ? { ...l, sort: u.sort, ...(u.stage ? { stage: u.stage } : {}) } : l;
    }));
    try {
      await api.reorder('leads', updates);
      updates.filter((u) => u.stage).forEach((u) => {
        const lead = before.find((l) => l._id === u.id);
        afterStageChange(lead, lead?.stage, u.stage);
      });
    } catch {
      setLeads(before);
      toast.error(t('ADMIN_CRM.COMMON.REORDER_FAILED'));
    }
  };

  /** Put a lead's new vendor (or none) everywhere it shows: the board and the open sheet. */
  const setVendor = (leadId: string, vendor: CrmLead['vendorInfo']) => {
    const patch = { vendor: vendor?._id || '', vendorInfo: vendor || null };
    setLeads((ls) => ls.map((l) => (l._id === leadId ? { ...l, ...patch } : l)));
    setEditing((e) => (e?._id === leadId ? { ...e, ...patch } : e));
  };

  const linkVendor = async (choice: CrmProspectChoice) => {
    if (!linking) return;
    setConverting(true);
    try {
      const result = await api.toProspect(linking._id, choice);
      setVendor(linking._id, result.vendor);
      const names = { name: result.vendor.name, alias: result.vendor.alias };
      toast.success(t(result.created ? 'ADMIN_CRM.PROSPECT.CREATED' : 'ADMIN_CRM.PROSPECT.LINKED', names));
      setLinking(null);
      if (result.login) setProspect(result);
    } catch (e: any) {
      toast.error(e?.message || t('ADMIN_CRM.PROSPECT.FAILED'));
    } finally {
      setConverting(false);
    }
  };

  const unlinkVendor = async (lead: Partial<CrmLead>) => {
    if (!lead._id) return;
    try {
      await api.unlinkVendor(lead._id);
      setVendor(lead._id, null);
      toast.success(t('ADMIN_CRM.PROSPECT.UNLINKED', { name: lead.vendorInfo?.name }));
    } catch {
      toast.error(t('ADMIN_CRM.PROSPECT.UNLINK_FAILED'));
    }
  };

  /** The vendor block of an open lead: the vendor it has (open, change, unlink), or a way to give it one. */
  const vendorSection = (lead: Partial<CrmLead> | null) => {
    if (!lead?._id) return null;
    const full = leads.find((l) => l._id === lead._id);
    const vendor = full?.vendorInfo;
    return (
      <div className="flex flex-col gap-1.5" data-testid="crm-lead-vendor">
        <span className="text-xs text-muted-foreground">{t('ADMIN_CRM.FIELDS.VENDOR')}</span>
        {vendor ? (
          <div className="flex min-w-0 flex-wrap items-center gap-2 rounded-lg border px-3 py-2">
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium">{vendor.name}</span>
              <span className="truncate font-mono text-xs text-muted-foreground">{vendor.alias}</span>
            </span>
            <Badge variant={vendor.kind === 'prospect' ? 'secondary' : 'outline'}>
              {t(vendor.kind === 'prospect' ? 'ADMIN_CRM.PROSPECT.KIND_PROSPECT' : 'ADMIN_CRM.PROSPECT.KIND_CUSTOMER')}
            </Badge>
            <Button size="xs" variant="outline" onClick={() => navigate(vendorPath(vendor._id))}>
              <ArrowUpRight data-icon="inline-start" />{t('ADMIN_CRM.PROSPECT.OPEN')}
            </Button>
            <Button size="xs" variant="outline" onClick={() => full && setLinking(full)} data-testid="crm-vendor-change">
              <Link2 data-icon="inline-start" />{t('ADMIN_CRM.PROSPECT.CHANGE')}
            </Button>
            <Button size="xs" variant="ghost" onClick={() => unlinkVendor(full!)} data-testid="crm-vendor-unlink">
              <Unlink data-icon="inline-start" />{t('ADMIN_CRM.PROSPECT.UNLINK')}
            </Button>
          </div>
        ) : (
          <Button variant="outline" className="justify-start" onClick={() => full && setLinking(full)} data-testid="crm-vendor-link">
            <Store data-icon="inline-start" />{t('ADMIN_CRM.PROSPECT.LINK_VENDOR')}
          </Button>
        )}
        {/* How the prospect tried their demo: the vendor is what its demos are made under. */}
        {vendor && <VendorDemoVisits vendorId={vendor._id} />}
      </div>
    );
  };

  /** The card's call to action: go to its vendor once it has one; turn it into a prospect while it is early. */
  const vendorAction = (l: CrmLead) => {
    if (l.vendorInfo) {
      return (
        <Button
          size="xs"
          variant="outline"
          className="mt-2 w-full"
          onPointerDown={stop}
          onClick={(e) => { stop(e); navigate(vendorPath(l.vendorInfo!._id)); }}
          data-testid={`crm-go-vendor-${l._id}`}
        >
          <ArrowUpRight data-icon="inline-start" />{t('ADMIN_CRM.PROSPECT.OPEN')}
        </Button>
      );
    }
    if (!PROSPECT_STAGES.includes(l.stage)) return null;
    const busy = converting && linking?._id === l._id;
    return (
      <Button
        size="xs"
        variant="outline"
        className="mt-2 w-full"
        disabled={busy}
        onPointerDown={stop}
        onClick={(e) => { stop(e); setLinking(l); }}
        data-testid={`crm-to-prospect-${l._id}`}
      >
        {busy ? <Loader2 data-icon="inline-start" className="animate-spin" /> : <Store data-icon="inline-start" />}
        {t(busy ? 'ADMIN_CRM.PROSPECT.CREATING' : 'ADMIN_CRM.PROSPECT.CREATE')}
      </Button>
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <KpiCard loading={loading} label={t('ADMIN_CRM.PIPELINE.KPI.OPEN')} value={kpis.open.length} hint={t('ADMIN_CRM.PIPELINE.KPI.OPEN_HINT')} />
        <KpiCard loading={loading} label={t('ADMIN_CRM.PIPELINE.KPI.VALUE')} value={fmtSplit(kpis.pipe)} hint={t('ADMIN_CRM.PIPELINE.KPI.VALUE_HINT')} />
        <KpiCard
          loading={loading}
          label={t('ADMIN_CRM.PIPELINE.KPI.MRR')}
          value={<span className="text-success" data-testid="crm-mrr">{fmtSplit(kpis.mrr)}</span>}
          hint={t('ADMIN_CRM.PIPELINE.KPI.MRR_HINT', { eur: eurEquivalent(kpis.mrr), count: kpis.active.length })}
        />
        <KpiCard
          loading={loading}
          label={t('ADMIN_CRM.PIPELINE.KPI.POTENTIAL')}
          value={eurEquivalent({ eur: kpis.mrr.eur + kpis.wait.eur, usd: kpis.mrr.usd + kpis.wait.usd })}
          hint={t('ADMIN_CRM.PIPELINE.KPI.POTENTIAL_HINT', { active: eurEquivalent(kpis.mrr), waiting: eurEquivalent(kpis.wait) })}
        />
        <KpiCard
          loading={loading}
          label={t('ADMIN_CRM.PIPELINE.KPI.WAITING')}
          value={<span className="text-warning">{fmtSplit(kpis.wait)}</span>}
          hint={t('ADMIN_CRM.PIPELINE.KPI.WAITING_HINT', { count: kpis.waiting.length })}
        />
        <KpiCard loading={loading} label={t('ADMIN_CRM.PIPELINE.KPI.CONTACTS')} value={contacts.total} hint={t('ADMIN_CRM.PIPELINE.KPI.CONTACTS_HINT', { count: contacts.replied })} />
        <KpiCard
          loading={loading}
          label={t('ADMIN_CRM.PIPELINE.KPI.OVERDUE')}
          value={<span className={kpis.overdue ? 'text-destructive' : 'text-success'}>{kpis.overdue}</span>}
          hint={t('ADMIN_CRM.PIPELINE.KPI.OVERDUE_HINT')}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchField value={q} onChange={setQ} placeholder={t('ADMIN_CRM.PIPELINE.SEARCH')} className="w-full sm:w-80" />
        <NativeSelect value={country} onChange={(e) => setCountry(e.target.value)} aria-label={t('ADMIN_CRM.COMMON.COUNTRY')}>
          <NativeSelectOption value="">{t('ADMIN_CRM.COMMON.ALL_COUNTRIES')}</NativeSelectOption>
          {countries.map((c) => <NativeSelectOption key={c} value={c}>{FLAGS[c] || ''} {c}</NativeSelectOption>)}
        </NativeSelect>
        <NativeSelect value={sort} onChange={(e) => setSort(e.target.value)} aria-label={t('ADMIN_CRM.PIPELINE.SORT')}>
          <NativeSelectOption value="manual">{t('ADMIN_CRM.PIPELINE.SORT_MANUAL')}</NativeSelectOption>
          <NativeSelectOption value="next">{t('ADMIN_CRM.PIPELINE.SORT_NEXT')}</NativeSelectOption>
          <NativeSelectOption value="value">{t('ADMIN_CRM.PIPELINE.SORT_VALUE')}</NativeSelectOption>
          <NativeSelectOption value="company">{t('ADMIN_CRM.PIPELINE.SORT_COMPANY')}</NativeSelectOption>
        </NativeSelect>
        <span className="flex-1" />
        <Button onClick={() => setEditing({ stage: 'prospect' })}><Plus />{t('ADMIN_CRM.PIPELINE.NEW')}</Button>
      </div>

      {!loading && (
        <CrmBoard<CrmLead>
          stages={LEAD_STAGES}
          items={shown}
          manualOrder={sort === 'manual'}
          onOpen={setEditing}
          onMoveStage={moveStage}
          onReorder={reorder}
          columnExtra={(col) => {
            const total = sumMoney(col.map((l) => l.value));
            return total.eur || total.usd ? `≈${fmtSplit(total)}` : null;
          }}
          renderCard={(l) => (
            <CrmCardBody country={l.country} title={l.company} next={l.next} due={l.due} tags={l.tags}>
              {l.contact && <p className="mt-0.5 truncate text-xs text-muted-foreground">{l.contact}</p>}
              {l.value && <p className="mt-1 text-xs font-medium text-success">{fmtMonthly(l.value)}</p>}
              {vendorAction(l)}
            </CrmCardBody>
          )}
        />
      )}

      <CrmRecordSheet<CrmLead>
        record={editing}
        fields={LEAD_FIELDS}
        required="company"
        title={{ create: t('ADMIN_CRM.PIPELINE.NEW'), edit: t('ADMIN_CRM.PIPELINE.EDIT') }}
        onSave={save}
        onDelete={remove}
        onClose={() => setEditing(null)}
        extra={vendorSection(editing)}
      />

      <CrmVendorDialog lead={linking} busy={converting} onClose={() => setLinking(null)} onConfirm={linkVendor} />
      <ProspectLoginDialog result={prospect} onClose={() => setProspect(null)} />
    </div>
  );
};
